import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { ArrowLeft, Download, Search, Building2 } from 'lucide-react';
import Spinner from './components/Spinner';
import { useFeedback360ResultsStore } from '@store/AssessmentCreator/Feedback360ResultsStore';
import './ConsolidatedResultSelection.css';

const ConsolidatedResultSelection: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  // Zustand store
  const {
    participants,
    loading,
    error,
    downloadError,
    isGenerating,
    fetchConsolidatedParticipants,
    downloadPDF,
    clearDownloadError,
  } = useFeedback360ResultsStore();

  // Local state
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [visibleCount, setVisibleCount] = useState(20); // Initial load: 20 users

  useEffect(() => {
    if (assessmentId) {
      fetchConsolidatedParticipants(Number(assessmentId));
    }
  }, [assessmentId]);

  // Handle domain filter - auto-select users when domain is entered
  useEffect(() => {
    if (domainFilter.trim()) {
      const usersFromDomain = participants
        .filter(p => p.userEmail.toLowerCase().includes(`@${domainFilter.toLowerCase()}`))
        .map(p => p.userId);
      setSelectedUserIds(usersFromDomain);
    } else {
      // Deselect all when domain filter is cleared
      setSelectedUserIds([]);
    }
  }, [domainFilter, participants]);

  // Reset visible count when search or filter changes
  useEffect(() => {
    setVisibleCount(20);
  }, [searchQuery, domainFilter]);

  const handleUserSelection = (userId: number) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === participants.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(participants.map(p => p.userId));
    }
  };

  const downloadConsolidatedResult = async () => {
    if (!assessmentId) return;

    // If no users selected, use all participants
    const userIdsToDownload = selectedUserIds.length === 0
      ? participants.map(p => p.userId)
      : selectedUserIds;

    const result = await downloadPDF(Number(assessmentId), {
      userIds: userIdsToDownload,
    });

    if (result.success) {
      // Navigate back after successful download
      setTimeout(() => {
        navigate(`/assessment-results/${assessmentId}`);
      }, 1000);
    }
  };

  // Filter participants based on search query AND domain filter
  const filteredParticipants = participants.filter(p => {
    // Search filter (name or email)
    const matchesSearch = searchQuery.trim() === '' ||
      p.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.userEmail.toLowerCase().includes(searchQuery.toLowerCase());

    // Domain filter (email domain)
    const matchesDomain = domainFilter.trim() === '' ||
      p.userEmail.toLowerCase().includes(`@${domainFilter.toLowerCase()}`);

    return matchesSearch && matchesDomain;
  });

  // Get visible participants for pagination
  const visibleParticipants = filteredParticipants.slice(0, visibleCount);
  const hasMore = visibleCount < filteredParticipants.length;

  // Load more handler
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 20);
  };

  if (loading) {
    return (
      <Layout>
        <div className="consolidated-selection" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="consolidated-selection" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="consolidated-error-message">{error}</div>
          <button className="consolidated-btn-retry" onClick={() => assessmentId && fetchConsolidatedParticipants(Number(assessmentId))}>
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="consolidated-selection" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="consolidated-selection-header">
          <button className="consolidated-btn-back" onClick={() => navigate(`/assessment-results/${assessmentId}`)}>
            <ArrowLeft size={16} /> Back to Results
          </button>
          <div className="consolidated-header-content">
            <h1 className="consolidated-selection-title">Consolidated Result</h1>
          </div>
          <p className="consolidated-selection-subtitle">
            Select participants to include in the consolidated PDF report
          </p>
        </div>

        {/* Selection Content */}
        <div className="consolidated-selection-content">
          <div className="consolidated-selection-count-bar">
            <div className="consolidated-selected-count-wrapper">
              <input
                type="checkbox"
                checked={selectedUserIds.length === participants.length && participants.length > 0}
                onChange={handleSelectAll}
                className="consolidated-select-all-checkbox"
              />
              <div className="consolidated-selected-count">
                <strong>{selectedUserIds.length}</strong> participant{selectedUserIds.length !== 1 ? 's' : ''} selected
              </div>
            </div>
            <button className="consolidated-btn-select-all" onClick={handleSelectAll}>
              {selectedUserIds.length === participants.length && participants.length > 0
                ? 'Deselect All'
                : `Select All (${participants.length})`}
            </button>
          </div>

          <div className="consolidated-selection-actions">
            <div className="consolidated-search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="consolidated-search-input"
              />
              {searchQuery && (
                <button className="consolidated-clear-search" onClick={() => setSearchQuery('')}>
                  ✕
                </button>
              )}
            </div>

            <div className="consolidated-search-box">
              <Building2 size={20} />
              <input
                type="text"
                placeholder="Filter by domain (e.g., gmail.com)"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                className="consolidated-search-input"
              />
              {domainFilter && (
                <button className="consolidated-clear-search" onClick={() => setDomainFilter('')}>
                  ✕
                </button>
              )}
            </div>
          </div>

          {filteredParticipants.length === 0 ? (
            <div className="consolidated-empty-state">
              <p>No participants found</p>
              {searchQuery && <p className="consolidated-empty-subtitle">Try adjusting your search terms</p>}
            </div>
          ) : (
            <>
              <div className="consolidated-user-list-container">
                <div className="consolidated-user-list">
                  {visibleParticipants.map(participant => (
                    <label key={participant.userId} className="consolidated-user-row">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(participant.userId)}
                        onChange={() => handleUserSelection(participant.userId)}
                      />
                      <div className="consolidated-user-details">
                        <div className="consolidated-user-name">{participant.userName}</div>
                        <span className="consolidated-user-separator">•</span>
                        <div className="consolidated-user-email">{participant.userEmail}</div>
                      </div>
                      <div className="consolidated-checkbox-indicator">
                        {selectedUserIds.includes(participant.userId) && <span>✓</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {hasMore && (
                <div className="consolidated-load-more-container">
                  <button className="consolidated-btn-load-more" onClick={handleLoadMore}>
                    Load More
                  </button>
                </div>
              )}

              <div className="consolidated-showing-count">
                Showing {visibleParticipants.length} of {filteredParticipants.length} participants
              </div>
            </>
          )}
        </div>

        {/* Selected Count and Error */}
        <div className="consolidated-selection-footer">
          {downloadError && (
            <div className="consolidated-error-message">
              {downloadError}
            </div>
          )}

          <div className="consolidated-action-buttons">
            <button
              className="consolidated-btn-cancel"
              onClick={() => navigate(`/assessment-results/${assessmentId}`)}
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              className="consolidated-btn-download"
              onClick={downloadConsolidatedResult}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <div className="consolidated-spinner-small"></div> Generating PDF...
                </>
              ) : (
                <>
                  <Download size={16} /> Download PDF ({selectedUserIds.length > 0 ? selectedUserIds.length : participants.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ConsolidatedResultSelection;
